// arrayUnion and setDoc might no longer be needed for saveToUserLibrary if we switch to addDoc for individual items.
// However, they ARE used elsewhere in this file (e.g. updateConversationInFirestore).
// For saveToUserLibrary, we'll primarily use collection, addDoc, serverTimestamp, doc (for user path).
import { collection, addDoc, serverTimestamp, updateDoc, doc, setDoc, getDoc, query, where, orderBy, limit, getDocs, runTransaction } from 'firebase/firestore';
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from './firebaseApp';
import { _config } from '../Constants';
import { setupUserProperties } from './tracking'; // Import getUserId from tracking
import { getUrlParameter, isLiteMode } from './urlUtils'; // Import the utility function
import { isTrialSession } from './trialSession';

import axios from 'axios';
import { initializeUserLevel, normalizeLevel } from './levelManager';
import { fetchLastPlacementTestResult } from './testResults';
import { syncLanguageChange } from './i18next';
import { clampProgressIndex, normalizeActivityProgressStatus, notifyActivityProgressUpdate } from './activitySelection';
const HTTP_memory = `${_config.url}memory`;
const HTTP_activityProgress = `${_config.url}activity-progress`;

// Firebase app, Firestore (db), and Auth are initialized in firebaseApp.js
// (imported above) so the whole client shares one database-pinned instance.
//
// There is no Realtime Database. It was decommissioned with the move to the
// madameai-canada Firestore database; user memory, preferences and test results
// all live in Firestore now. Do not reintroduce a getDatabase() call here — the
// instance no longer exists, so every read silently returned nothing and every
// write silently dropped (MAD-788).

// Firestore document ID
let firestoreDocId = null;
let createdAtLocal = null; // Store createdAt timestamp locally to calculate the total duration

let userMemory = null; // Store user memory locally
let userName = null; // Store user name locally
let userLevel = null; // Store user level from Firestore/placement test (1-12 Quebec scale)
let targetLanguage = null; // Store target language to learn (e.g., 'fr', 'en', 'es')
let lastPlacementTest = null; // Store last placement test result with date
let userWork = null; // Store user work information locally
let organizationId = null; // Store organizationId from URL params (for multi-org performance optimization)
let organizationName = null; // Store organization name fetched once at auth time
let userActivityAccess = null; // { customBucket, hasPitch } from the users doc (null until read)

/** AUTHENTICATION */

// NOTE: Automatic anonymous sign-in has been removed.
// All users must now explicitly authenticate via:
// 1. URL token (?token=xxx) - exchanged for Firebase custom token
// 2. Email/password login on AuthPage
// 3. Anonymous login button (only visible with ?anonymous=true)
// See authenticateUser() for the authentication flow.

// Function to ensure user has timezone set in Firestore
// @param {boolean} hasTimezone - If true, skip the read (already confirmed timezone exists)
const ensureUserTimezone = async (userId, hasTimezone = false) => {
  if (hasTimezone) return; // Already set, nothing to do
  try {
    const userDocRef = doc(db, 'users', userId);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
    await setDoc(userDocRef, {
      timezone: userTimezone,
      createdAt: serverTimestamp()
    }, { merge: true });
    console.log('User timezone initialized in Firestore:', userTimezone);
  } catch (error) {
    console.error('Error ensuring user timezone:', error);
  }
};

// Listen for authentication state changes
onAuthStateChanged(auth, async (user) => {
  if (user) {
    console.log('User is logged in:', user.uid);
    // A real (non-anonymous) account — e.g. someone who joined an organization — is not a trial
    // lead. Clear the trial-lead artifacts left in this browser so they are no longer gated and
    // their conversation docs / analytics stop carrying the stale captured contact.
    if (!user.isAnonymous) {
      localStorage.removeItem('trialSession');
      localStorage.removeItem('capturedEmail');
      localStorage.removeItem('capturedFirstName');
      localStorage.removeItem('trialSessionCount'); // TRIAL_SESSION_COUNT_KEY in trialLimits.js
    }
    // Cache organizationId from URL params (if present) for multi-org performance optimization
    const urlParams = new URLSearchParams(window.location.search);
    organizationId = urlParams.get('organizationId') || null;
    if (organizationId) {
      // Fetch org name once at auth time so it can be passed with every upload
      getDoc(doc(db, 'organizations', organizationId))
        .then(orgDoc => {
          organizationName = orgDoc.data()?.name || null;
          console.log('Cached organization name from Firestore:', organizationName);
          setupUserProperties();
        })
        .catch(() => {});
    }

    if (!isLiteMode()) {
      // Single Firestore read for language, targetLanguage, level, timezone, and custom bucket
      const profile = await fetchUserFirestoreProfile(user.uid);
      targetLanguage = profile.targetLanguage;
      userActivityAccess = profile.activityAccess;
      userLevel = await initializeUserLevel(user.uid, profile.level); // Uses pre-fetched level, falls back to placement test
      await ensureUserTimezone(user.uid, profile.hasTimezone);

      // Per-user documents that live outside the profile doc, so they can't be
      // folded into the fetchUserFirestoreProfile read above.
      userMemory = await fetchCurrentUserMemory();
      userName = getUrlParameter('name') || await fetchCurrentUserName();
      lastPlacementTest = await fetchLastPlacementTestResult();
      userWork = await fetchCurrentUserWork();
    }
    setupUserProperties(); // Identify user in tracking : PostHog, Sentry etc.
  } else {
    console.warn('User is not logged in.');
  }
});

let isProcessingCustomToken = false;

export const authenticateUser = (setIsAuthenticated) => {
  const auth = getAuth();
  const tokenFromUrl = getUrlParameter('token');

  onAuthStateChanged(auth, (user) => {
    // Only authenticate with custom token if it's provided in URL (fresh token)
    if (tokenFromUrl && !isProcessingCustomToken) {
      if (user && !user.isAnonymous) {
        console.log('Custom token provided but user already authenticated:', user.uid);
        console.log('Signing out existing user to authenticate with custom token');
        isProcessingCustomToken = true;
        signOut(auth).then(() => {
          console.log('Signed out, now authenticating with custom token');
          authenticateWithCustomToken(tokenFromUrl, setIsAuthenticated);
        }).catch(console.error);
        return;
      }
      
      isProcessingCustomToken = true; // Set flag before processing
      authenticateWithCustomToken(tokenFromUrl, setIsAuthenticated);
    } else if (user) {
      console.log('User authenticated with UID:', user.uid);
      setIsAuthenticated(true);
      isProcessingCustomToken = false; // Reset flag when successfully authenticated
    } else if (!isProcessingCustomToken) {
      // No automatic anonymous auth - require explicit login for all users
      // Users can login via: email/password OR anonymous button (if ?anonymous=true)
      setIsAuthenticated(false);
    }
  });
};

function authenticateWithCustomToken(token, setIsAuthenticated) {
  console.log('Attempting custom token authentication...');
  console.log('About to sign in with custom token. Token preview:', token.substring(0, 50) + '...');
  
  signInWithCustomToken(auth, token)
    .then((userCredential) => {
      console.log('Custom token authentication successful!');
      console.log('- Authenticated UID:', userCredential.user.uid);
      console.log('- User email:', userCredential.user.email);
      console.log('- Is anonymous:', userCredential.user.isAnonymous);
      setIsAuthenticated(true);
      isProcessingCustomToken = false; // Reset flag on success
    })
    .catch((error) => {
      console.error('Error authenticating with token:', error);
      setIsAuthenticated(false);
      isProcessingCustomToken = false; // Reset flag on error
    });
}

/**
 * Re-establishes a partner session from a `?token=` still present in the URL.
 *
 * Partners embed us in a cross-site iframe, where the Firebase session lives in
 * partitioned third-party storage and does not reliably survive a full-page
 * redirect. Pages that land after one of our own redirects call this before
 * concluding the learner is signed out and bouncing them to the login screen.
 *
 * @returns {Promise<import('firebase/auth').User | null>} the signed-in user, or
 *   null when there is no token to use or it is expired/invalid
 */
export const recoverPartnerSession = async () => {
  const token = getUrlParameter('token');

  if (!token) {
    return null;
  }

  try {
    const credential = await signInWithCustomToken(auth, token);
    console.log('Recovered partner session for UID:', credential.user.uid);

    return credential.user;
  } catch (error) {
    console.error('Partner token sign-in failed:', error);

    return null;
  }
};

export const signOutUser = () => {
  const auth = getAuth();
  signOut(auth)
    .then(() => {
      window.location.reload(); // Refresh the app
    })
    .catch((error) => {
      console.error('Error signing out:', error);
    });
};

function getCurrentUserId() {
  const user = auth.currentUser;
  if (user) {
    return user.uid; // Return the user ID (UID)
  } else {
    console.warn('No user is currently logged in.');
    return null;
  }
}

// The activities the users doc unlocks: custom-document activities (custom_bucket,
// premium users) and pitch practice (a non-blank pitch)
const readActivityAccess = (userData) => ({
  customBucket: userData?.custom_bucket || null,
  hasPitch: typeof userData?.pitch === 'string' && userData.pitch.trim() !== '',
});

// One cached users-doc read for everything the activities page unlocks.
// A failed read is not cached, so the next call tries again.
async function getUserActivityAccess() {
  if (userActivityAccess) {
    return userActivityAccess;
  }

  const userId = getCurrentUserId();
  if (!userId) {
    return readActivityAccess(null);
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    userActivityAccess = readActivityAccess(userDoc.exists() ? userDoc.data() : null); // Cache it
    return userActivityAccess;
  } catch (error) {
    console.error('Error fetching user activity access:', error);
    return readActivityAccess(null);
  }
}

// Function to check if user has custom_bucket
async function getUserCustomBucket() {
  const { customBucket } = await getUserActivityAccess();
  return customBucket;
}

function getCurrentUserEmail() {
  const user = auth.currentUser;
  if (user) {
    return user.email; // Return the user email (UID)
  } else {
    console.warn('No user is currently logged in.');
    return null;
  }
}

/** CONVERSATION */

const createConversationDoc = async (
  userLevel,
  userAge,
  userLanguage,
  selectedActivity = undefined, // Optional
  targetLanguage,
  capturedFirstName,
  consentToFollowUp, 
  capturedEmail
) => {
  if (!isLiteMode()) {
    userMemory = await fetchCurrentUserMemory(); // refresh userMemory to properly handle the new selectedActivity
    // Partner integration: URL parameter takes priority over stored name
    userName = getUrlParameter('name') || await fetchCurrentUserName();
  }

  try {
    // Fallback to localStorage values if parameters are not provided
    userLevel = userLevel ?? localStorage.getItem('userLevel') ?? 'unknown';
    userAge = userAge ?? localStorage.getItem('userAge') ?? 'unknown';
    userLanguage = userLanguage ?? localStorage.getItem('userLanguage') ?? 'unknown';
    selectedActivity = selectedActivity ?? localStorage.getItem('selectedActivity') ?? undefined;
    targetLanguage = targetLanguage ?? localStorage.getItem('targetLanguage') ?? undefined;
    capturedFirstName = capturedFirstName ?? localStorage.getItem('capturedFirstName') ?? undefined;
    consentToFollowUp = consentToFollowUp ?? localStorage.getItem('consentToFollowUp') ?? undefined;
    capturedEmail = capturedEmail ?? localStorage.getItem('capturedEmail') ?? undefined;

    // Get partner integration fields from URL parameters
    const partner = getUrlParameter('partner') || null;
    const partnerUserId = getUrlParameter('uid') || null;
    const partnerActivity = getUrlParameter('activity') || null;
    const partnerWorkDomain = getUrlParameter('work_domain') || null;
    const partnerWorkTitle = getUrlParameter('work_title') || null;

    const docData = {
      conversation: [],
      createdAt: serverTimestamp(),
      userId: getCurrentUserId() ?? 'unknown',
      userLevel: userLevel ?? 'unknown',
      userAge: userAge ?? 'unknown',
      userLanguage: userLanguage ?? 'unknown',
      targetLanguage: targetLanguage ?? 'unknown',
      // The learner's explicit "Audio model" choice (default or conversaflex);
      // null = never chose, so the server-side UNIFIED_AUDIO_MODEL default
      // applied. Replaces the sttModel field, which recorded a selector that
      // never reached the backend.
      audioModel: localStorage.getItem('audioModel') || null,
      capturedEmail: capturedEmail ?? localStorage.getItem('capturedEmail') ?? null,
      capturedFirstName: capturedFirstName ?? localStorage.getItem('capturedFirstName') ?? null,
      consentToFollowUp: consentToFollowUp ?? localStorage.getItem('consentToFollowUp') ?? null,
      newsletterOptIn: localStorage.getItem('newsletterOptIn') ?? null,
      // Partner integration fields (only include if present)
      ...(partner && { partner }),
      ...(partnerUserId && { partnerUserId }),
      ...(partnerActivity && { partnerActivity }),
      ...(partnerWorkDomain && { work_domain: partnerWorkDomain }),
      ...(partnerWorkTitle && { work_title: partnerWorkTitle }),
      // Trial lead-gen session marker (analytics only).
      ...(isTrialSession() && { trial: true }),
    };

    // Only add selectedActivity if it's defined and not a SyntheticEvent
    if (
      selectedActivity !== undefined &&
      typeof selectedActivity !== 'object'
    ) {
      docData.selectedActivity = selectedActivity;
    }

    const docRef = await addDoc(collection(db, 'conversations'), docData);

    console.log('Document written with ID: ', docRef.id);
    firestoreDocId = docRef.id; // Store the Firestore document ID internally
    // A real local clock reading, not serverTimestamp(): that returns a FieldValue
    // sentinel the server resolves on write, so it is not a number and cannot be
    // subtracted from. Doing so is what made every stored `duration` NaN.
    createdAtLocal = Date.now();

    return docRef.id;
  } catch (error) {
    console.error('Error adding document: ', error);
    throw error;
  }
};

const updateConversationInFirestore = async (conversation, feedbackData) => {
  if (!firestoreDocId) {
    console.error('No Firestore document ID found');
    try {
      await createConversationDoc(); // Create a document ID for next time
    } catch (error) {
      console.error('Error adding document: ', error);
      throw error;
    }
    return;
  }

  try {
    const updateData = {};

    if (conversation && conversation.length > 0) {
      // Replace the whole array rather than arrayUnion: every caller passes the
      // full in-memory history, and messages MUTATE after being written (the
      // text-first flow adds the TTS audio URL to the assistant turn once it's
      // ready). arrayUnion can't update an element in place, so the mutated
      // copy no longer matched and was appended again — every assistant turn
      // was stored twice (once audio:null, once with the URL).
      updateData.conversation = conversation;
      updateData.timestamp = serverTimestamp();
      updateData.lastEditedAt = serverTimestamp(); // Update lastEditedAt timestamp

      // Guarded because the two are set in different functions: without a start
      // time this would record "seconds since 1970" rather than a session length.
      if (createdAtLocal) {
        updateData.duration = Math.floor((Date.now() - createdAtLocal) / 1000);
      }
    }

    // Add user feedback if provided
    if (feedbackData) {
      updateData.feedback = {
        rating: feedbackData.rating || 0,
        tags: feedbackData.tags || [],
        comment: feedbackData.comment || '',
        submittedAt: serverTimestamp()
      };
    }

    if (Object.keys(updateData).length > 0) {
      const docRef = doc(db, 'conversations', firestoreDocId);
      await updateDoc(docRef, updateData);
      console.log('Conversation updated in Firestore with ID: ', firestoreDocId);
    } else {
      console.log('No updates to apply.');
    }
  } catch (error) {
    console.error('Error updating conversation in Firestore: ', error);
  }
};

export const updateFeedbackInFirestore = async (conversationId, feedbackData) => {
  if (!conversationId || !feedbackData) return;
  try {
    const docRef = doc(db, 'conversations', conversationId);
    await updateDoc(docRef, {
      feedback: {
        rating: feedbackData.rating || 0,
        tags: feedbackData.tags || [],
        comment: feedbackData.comment || '',
        submittedAt: serverTimestamp()
      }
    });
  } catch (error) {
    console.error('Error updating feedback in Firestore:', error);
  }
};

/** USER PREFERENCES */

// Function to save user preferences to Firestore
export const saveUserPreferences = async (age, language, targetLanguage, level, languageVariant, ttsSpeed) => {

  const userId = getCurrentUserId()
  if (userId) {
    try {
      // Save all user preferences to Firestore users collection (primary storage)
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
      const firestoreUserRef = doc(db, 'users', userId);
      await setDoc(firestoreUserRef, {
        timezone: userTimezone,
        age: age,
        language: language, // User's native language (UI language)
        targetLanguage: targetLanguage,
        level: level,
        languageVariant: languageVariant || 'france',
        ttsSpeed: ttsSpeed || 1.0,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('User preferences saved to Firestore:', { age, language, level, languageVariant, ttsSpeed, timezone: userTimezone });

      // Update cache to keep it in sync with Firebase
      if (level !== null && level !== undefined) {
        updateCachedUserLevel(level);
      }

    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  }

  // Whether we are logged in or not, save to local Storage
  localStorage.setItem('userAge', age)
  localStorage.setItem('userLanguage', language)
  localStorage.setItem('userLevel', level)
  localStorage.setItem('languageVariant', languageVariant || 'france');
  localStorage.setItem('targetLanguage', targetLanguage);
  localStorage.setItem('ttsSpeed', ttsSpeed || 1.0);
};

// function to update the languageVariant in Firestore and localStorage
export const updateLanguageVariant = async (languageVariant) => {
  const userId = getCurrentUserId();
  if (userId) {
    try {
      // Save to Firestore (primary storage)
      const firestoreUserRef = doc(db, 'users', userId);
      await setDoc(firestoreUserRef, {
        languageVariant: languageVariant,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('Language variant updated in Firestore:', languageVariant);
    } catch (error) {
      console.error('Error updating Language variant:', error);
    }
  }

  localStorage.setItem('languageVariant', languageVariant);
};

// function to update the preferredPronoun in Firestore and localStorage
// Valid values: 'masculine', 'feminine', 'both'
export const updatePreferredPronoun = async (preferredPronoun) => {
  const userId = getCurrentUserId();
  if (userId) {
    try {
      // Save to Firestore (primary storage)
      const firestoreUserRef = doc(db, 'users', userId);
      await setDoc(firestoreUserRef, {
        preferredPronoun: preferredPronoun,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('Preferred pronoun updated in Firestore:', preferredPronoun);
    } catch (error) {
      console.error('Error updating preferred pronoun:', error);
    }
  }

  localStorage.setItem('preferredPronoun', preferredPronoun);
};

// Function to save notification preferences to Firestore
// Structure: { enabled: boolean, reminderDays: [7 booleans], reminderTime: 'HH:MM', channels: { email: bool, sms: bool } }
export const saveNotificationPreferences = async (notificationPreferences) => {
  const userId = getCurrentUserId();
  if (userId && notificationPreferences) {
    try {
      // Save to Firestore (primary storage)
      const firestoreUserRef = doc(db, 'users', userId);
      await setDoc(firestoreUserRef, {
        notifications: notificationPreferences,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('Notification preferences saved to Firestore:', notificationPreferences);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    }
  }

  // Also save to localStorage as backup
  localStorage.setItem('notificationPreferences', JSON.stringify(notificationPreferences));
};

export function hasNotificationPreferences() {
  return !!localStorage.getItem('notificationPreferences');
}

// Persist the contact info captured during a free trial (direct activity / lite)
// to the user's Firestore profile. Trial users are anonymous, but they still get a
// users/{uid} doc — writing here keeps a single canonical record (email + newsletter
// opt-in, plus native/target language for segmentation) per user instead of only
// scattering it across conversation docs. Uses the same `language`/`targetLanguage`
// field names as saveUserPreferences so the profile reader picks them up.
export const saveCapturedContact = async ({ email, firstName, consentToFollowUp, newsletterOptIn, language, targetLanguage, workIntent } = {}) => {
  const userId = getCurrentUserId();
  if (!userId || !email) return;

  try {
    const firestoreUserRef = doc(db, 'users', userId);
    const data = { capturedEmail: email, updatedAt: serverTimestamp() };
    if (firstName) data.capturedFirstName = firstName;
    if (consentToFollowUp !== undefined) data.consentToFollowUp = consentToFollowUp;
    if (newsletterOptIn !== undefined) data.newsletterOptIn = newsletterOptIn;
    if (language) data.language = language;
    if (targetLanguage) data.targetLanguage = targetLanguage;
    if (workIntent) data.workIntent = workIntent;

    await setDoc(firestoreUserRef, data, { merge: true });
    console.log('Captured contact saved to Firestore for user:', userId);
  } catch (error) {
    console.error('Error saving captured contact:', error);
  }
};

// Function to fetch user preferences from Firestore
export const fetchUserPreferences = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user is currently logged in, cannot fetch preferences.');
    return null;
  }

  const userId = user.uid;

  try {
    // Primary: Fetch from Firestore
    const firestoreUserRef = doc(db, 'users', userId);
    const firestoreDoc = await getDoc(firestoreUserRef);

    if (firestoreDoc.exists()) {
      const data = firestoreDoc.data();
      console.log('User preferences fetched from Firestore:', data);
      return {
        age: data.age,
        language: data.language,
        targetLanguage: data.targetLanguage,
        level: data.level,
        languageVariant: data.languageVariant,
        preferredPronoun: data.preferredPronoun || 'both'
      };
    }

    // User not found in Firestore - determine level using tier system
    console.log('User preferences not found in Firestore, determining level...');
    let determinedLevel = null;

    // 1. Try placement test first (if valid - over 4 interactions)
    try {
      const testResult = await fetchLastPlacementTestResult();
      if (testResult?.estimated_level?.score_average) {
        // score_average is already in 1-12 Quebec scale (from AI prompt)
        determinedLevel = Math.round(testResult.estimated_level.score_average);
        // Ensure it's within valid range
        determinedLevel = Math.min(12, Math.max(1, determinedLevel));
        console.log('Level determined from placement test:', determinedLevel);
      }
    } catch (error) {
      console.warn('Could not fetch placement test:', error);
    }

    // 2. Fallback to localStorage if no test result
    if (!determinedLevel) {
      const storedLevel = localStorage.getItem('userLevel');
      determinedLevel = normalizeLevel(storedLevel);
      if (determinedLevel) {
        console.log('Level determined from localStorage:', determinedLevel);
      }
    }

    // 3. Final fallback to 3 (beginner level)
    if (!determinedLevel) {
      determinedLevel = 3;
      console.log('No valid level found, using default:', determinedLevel);
    }

    // Return level without Firestore data (will be saved when user sets preferences in Modal)
    return {
      level: determinedLevel
    };
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return null;
  }
};

/** MEMORY MANAGEMENT */

/* private */
async function _getUserMemory(userId, activityId, organizationId = null) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const idToken = await user.getIdToken();
    let url = HTTP_memory;
    const params = [];
    if (activityId) {
      params.push(`activityId=${activityId}`);
    }
    if (organizationId) {
      params.push(`organizationId=${organizationId}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${idToken}`, // Add ID token to headers
        },
      });
      return response.data
    } catch (error) {
      console.error('Error fetching user memory:', error.response?.data?.error || error.message)
      return null
    }
  } catch (error) {
    console.error('Error fetching user memory:', error);
    if (error.message === 'Unauthorized') {
      return {
        statusCode: 401,
        body: JSON.stringify({ error: 'Unauthorized' }),
      };
    } else {
      console.error('Error handling user memory:', error);
      return {
        statusCode: 500,
        body: JSON.stringify({ error: 'Internal server error' }),
      };
    }
  }
}

// Example usage
async function fetchCurrentUserMemory() {
  console.log("Fetching user memory...");
  const user = auth.currentUser;
  if (user) {
    const userId = user.uid;
    const activityId = localStorage.getItem('selectedActivity') || null;
    // Get organizationId from URL params to support multi-organization users
    const urlParams = new URLSearchParams(window.location.search);
    const organizationId = urlParams.get('organizationId') || null;
    const userMemory = await _getUserMemory(userId, activityId, organizationId);
    return userMemory;
  } else {
    console.warn('No user is currently logged in.');
    return null;
  }
}

// Reads the user's memory doc from Firestore (user_memory/{uid}) on the
// madameai-canada database. This used to fall back to a Realtime Database node
// for un-migrated users; that database no longer exists, so the fallback could
// only ever return null while making a doomed network call on every miss.
async function fetchUserMemoryData(userId) {
  const fsSnap = await getDoc(doc(db, 'user_memory', userId));
  return fsSnap.exists() ? fsSnap.data() : null;
}

async function fetchCurrentUserName() {

  const user = auth.currentUser;
  if (!user) {
    return;
  }
  const userId = user.uid;

  try {
    const memoryData = await fetchUserMemoryData(userId);
    if (memoryData) {
      return memoryData.name || null;
    }
    console.warn('No name data found for user:', userId);
    return null;
  } catch (error) {
    console.warn('Could not load name from user memory:', error.code || error.message);
    return null;
  }
}

async function fetchCurrentUserWork() {

  const user = auth.currentUser;
  if (!user) {
    return;
  }
  const userId = user.uid;

  try {
    const memoryData = await fetchUserMemoryData(userId);
    if (memoryData) {
      return { work_title: memoryData.work_title || null, work_domain: memoryData.work_domain || null };
    }
    console.warn('No work data found for user:', userId);
    return {};
  } catch (error) {
    console.warn('No work data found for user:', error);
    return {};
  }
}

/**
 * Fetches the user's Firestore profile in a single read.
 * Extracts language, targetLanguage, level, and timezone from users/{uid}.
 * Syncs each field to its appropriate store (localStorage, i18next, etc.)
 *
 * @param {string} userId - The Firebase user ID
 * @returns {Promise<{language: string|null, targetLanguage: string|null, level: number|null, hasTimezone: boolean}>}
 */
async function fetchUserFirestoreProfile(userId) {
  const result = { language: null, targetLanguage: null, level: null, hasTimezone: false, activityAccess: null };

  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return result;
    }

    const data = userDoc.data();

    // Native/UI language
    if (data.language) {
      console.log('Native language retrieved from Firestore:', data.language);
      syncLanguageChange(data.language);
      result.language = data.language;
    }

    // Target language (what the user is learning)
    if (data.targetLanguage) {
      console.log('Target language retrieved from Firestore:', data.targetLanguage);
      localStorage.setItem('targetLanguage', data.targetLanguage);
      result.targetLanguage = data.targetLanguage;
      window.dispatchEvent(new CustomEvent('targetLanguageChanged', {
        detail: { targetLanguage: data.targetLanguage }
      }));
    }

    // Level (normalize via levelManager)
    const normalized = normalizeLevel(data.level);
    if (normalized !== null) {
      console.log('Level retrieved from Firestore:', normalized, '(raw:', data.level + ')');
      result.level = normalized;
    }

    // Timezone presence (so caller can set it if missing)
    result.hasTimezone = !!data.timezone;

    // Custom bucket (premium users) and sales pitch, which unlock activities
    result.activityAccess = readActivityAccess(data);

    // Notification preferences — sync to localStorage so components can check synchronously
    if (data.notifications) {
      localStorage.setItem('notificationPreferences', JSON.stringify(data.notifications));
    }
  } catch (error) {
    console.warn('Error fetching user Firestore profile:', error);
  }

  return result;
}

/**
 * Saves the user's target language preference to Firestore
 * @param {string} language - Language code (e.g., 'fr', 'en', 'es')
 * @returns {Promise<boolean>} True if saved successfully
 */
/**
 * Write a single field (plus a `${field}UpdatedAt` timestamp) to the user doc
 * users/{uid}, creating the doc if needed. setDoc(..., { merge: true }) upserts,
 * so no separate create/not-found handling is needed.
 *
 * @param {string} field - field name, e.g. 'language' or 'targetLanguage'
 * @param {*} value
 * @param {Object} [opts]
 * @param {boolean} [opts.onlyIfUnset] - when true, skip the write if the field
 *   already holds a truthy value. Runs in a transaction so a concurrent explicit
 *   write can't be clobbered by this (default) write.
 * @returns {Promise<boolean>} whether a write actually happened
 */
async function saveUserDocField(field, value, { onlyIfUnset = false } = {}) {
  const user = auth.currentUser;
  if (!user) {
    console.warn(`No authenticated user - cannot save ${field}`);
    return false;
  }
  const userDocRef = doc(db, 'users', user.uid);
  const payload = { [field]: value, [`${field}UpdatedAt`]: serverTimestamp() };

  try {
    if (onlyIfUnset) {
      const wrote = await runTransaction(db, async (tx) => {
        const snap = await tx.get(userDocRef);
        if (snap.exists() && snap.data()?.[field]) return false; // leave existing value untouched
        tx.set(userDocRef, payload, { merge: true });
        return true;
      });
      if (wrote) console.log(`${field} saved to Firestore (default; was unset):`, value);
      return wrote;
    }
    await setDoc(userDocRef, payload, { merge: true });
    console.log(`${field} saved to Firestore:`, value);
    return true;
  } catch (error) {
    console.error(`Error saving ${field} to Firestore:`, error);
    return false;
  }
}

/**
 * Persist the user's target language ("targetLanguage") immediately, and keep the
 * in-memory cache (read by getUserTargetLanguage) in sync.
 */
export async function saveUserTargetLanguage(language) {
  const ok = await saveUserDocField('targetLanguage', language);
  if (ok) targetLanguage = language;
  return ok;
}

/**
 * Persist the user's native language ("language") immediately. Called from the
 * onboarding NativeLanguageStep so the choice survives an early exit (before
 * ReadyStep) — otherwise the field stayed unset and the learning plan / activities
 * fell back to English.
 */
export const saveUserNativeLanguage = (language) => saveUserDocField('language', language);

/**
 * Persist a *guessed default* native language only if the user has none saved yet
 * (transaction-guarded), so accepting the preselected default and then leaving
 * early still records a mother tongue without overwriting a returning user's real,
 * previously-chosen language.
 */
export const saveUserNativeLanguageIfUnset = (language) =>
  saveUserDocField('language', language, { onlyIfUnset: true });

export const getFirestoreDocId = () => firestoreDocId;
export const resetFirestoreDocId = () => {
  console.log('Resetting firestoreDocId to null');
  firestoreDocId = null;
};
export const getUserMemory = () => userMemory;
export const getCurrentUserName = () => getUrlParameter('name') || userName; // Partner integration: URL param takes priority
export const getCurrentUserLevel = () => userLevel; // Returns user level (1-12 Quebec scale)
export const getLastPlacementTest = () => lastPlacementTest; // Returns cached placement test result
export const getUserWork = () => userWork;
export const getUserTargetLanguage = () => targetLanguage; // Returns user's target language to learn (e.g., 'fr', 'en', 'es')
export const getCurrentOrganizationId = () => organizationId; // Returns organizationId from URL params (for multi-org performance)
export const getCurrentOrganizationName = () => organizationName; // Returns org name fetched once at auth time

/**
 * Fresh Firebase ID token, or null when no user is signed in or the fetch fails.
 * Callers treat null as "cannot authenticate this request" — e.g. the streamed turn
 * path throws pre-audio on null so it falls back to the legacy /upload wholesale.
 */
export const getIdTokenOrNull = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch (error) {
    console.warn('Failed to fetch Firebase ID token:', error?.message);
    return null;
  }
};


/**
 * Get organization childrenMode flag
 * @param {string} organizationId - The organization ID
 * @returns {Promise<boolean>} - true if childrenMode is enabled
 */
export const getOrganizationChildrenMode = async (organizationId) => {
  if (!organizationId) return false;
  try {
    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    return orgDoc.exists() && orgDoc.data().childrenMode === true;
  } catch (error) {
    console.error('Error fetching organization:', error);
    return false;
  }
};

/**
 * Updates the cached user level after placement test completion
 * @param {number} newLevel - The new level (1-12 Quebec scale)
 */
export const updateCachedUserLevel = (newLevel) => {
  userLevel = newLevel;
  console.log('User level cache updated to:', newLevel);
};

/**
 * Updates the cached placement test result after test completion
 * @param {Object} testResult - The placement test result { date, estimated_level, score }
 */
export const updateCachedPlacementTest = (testResult) => {
  lastPlacementTest = testResult;
  console.log('Placement test cache updated:', testResult.date);
};

/** USER LIBRARY MANAGEMENT */

/**
 * Saves a selected piece of text to the user's personal library in Firestore.
 * @param {string} userId - The ID of the user.
 * @param {string} selectedText - The text selected by the user.
 * @param {string} contextMessage - The full message content from which the text was selected.
 * @param {string} conversationId - The ID of the conversation where the text was selected.
 */
export const saveToUserLibrary = async (userId, selectedText, contextMessage, conversationId) => {
  if (!userId || !selectedText) {
    console.error('User ID and selected text are required to save to library.');
    throw new Error('User ID and selected text are required.');
  }
  if (!conversationId) {
    console.warn('Conversation ID not provided for vocabulary entry. Saving without it.');
    // Or: throw new Error('Conversation ID is required.');
  }

  // Path will be users/{userId}/vocabulary
  // Each item will be a new document in this subcollection.
  const userVocabCollectionRef = collection(db, 'users', userId, 'vocabulary');

  const newItem = {
    text: selectedText,
    context: contextMessage,
    conversationId: conversationId || null, // Store conversationId, default to null if not provided
    timestamp: serverTimestamp(), // Correctly used with addDoc
  };

  try {
    // addDoc creates a new document with an auto-generated ID within the userVocabCollectionRef
    const docRef = await addDoc(userVocabCollectionRef, newItem);

    console.log(`Item saved to user vocabulary with ID: ${docRef.id} for user ${userId}: "${selectedText}" from conversation ${conversationId}`);
  } catch (error) {
    console.error('Error saving item to user vocabulary:', error);
    throw new Error('Failed to save item to vocabulary.');
  }
};

/** ACTIVITY PROGRESS TRACKING */

/**
 * Sends one attempt event to the /activity-progress endpoint and refreshes
 * the local progress cache from its response. Cards and the plan bar show
 * bestProgressIndex (best attempt); the in-session bar uses the returned
 * attemptProgressIndex.
 * @param {string} userId - The ID of the user
 * @param {string} activityId - The activity ID (e.g., "1", "2", etc.)
 * @param {string} conversationId - The conversation doc id (attempt key)
 * @param {Object} payload - { event: 'start'|'progress'|'complete'|'end', progressIndex?, messages? }
 */
export const updateActivityProgress = async (userId, activityId, conversationId, payload = {}) => {
  if (!userId || !activityId || !conversationId) {
    console.error('userId, activityId, and conversationId are required for activity progress tracking');
    return null;
  }

  try {
    const user = auth.currentUser;
    const idToken = user ? await user.getIdToken() : null;
    if (!idToken) {
      throw new Error('Missing Firebase auth token for activity progress update');
    }

    const response = await axios.post(
      HTTP_activityProgress,
      {
        userId,
        activityId: String(activityId),
        conversationId,
        event: payload.event || 'progress',
        ...(payload.progressIndex !== undefined && payload.progressIndex !== null
          ? { progressIndex: clampProgressIndex(payload.progressIndex) }
          : {}),
        ...(Array.isArray(payload.messages) && payload.messages.length > 0
          ? { messages: payload.messages }
          : {}),
        targetLanguage: targetLanguage || localStorage.getItem('targetLanguage') || 'French',
        userLanguage: localStorage.getItem('userLanguage') || 'en',
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = response.data || {};
    const bestProgressIndex = clampProgressIndex(result.bestProgressIndex);
    const status = normalizeActivityProgressStatus(result.status, bestProgressIndex);

    if (!result.estimateFailed) {
      notifyActivityProgressUpdate({
        userId,
        activityId: String(activityId),
        progress: {
          status,
          progressIndex: bestProgressIndex,
          lastActivityAt: Date.now(),
          ...(status === 'completed' ? { completedAt: Date.now() } : {}),
          activityDocId: result.activityDocId,
        },
      });
    }

    return {
      ...result,
      activityId: String(result.activityId || activityId),
      status,
      bestProgressIndex,
      attemptProgressIndex: clampProgressIndex(result.attemptProgressIndex),
    };

  } catch (error) {
    console.error('Error updating activity progress:', error.response?.data || error);
    return null;
  }
};

/**
 * Fetches one learning_activities doc — used only when the user opens the
 * activity details modal. List views read the aggregate progress mirrored
 * into the planner doc instead of fetching per activity.
 * @param {string} activityDocId - `${plannerId}_${activityNumber}`
 * @returns {Object|null} The doc data, or null when missing/unreadable
 */
export const getLearningActivityDoc = async (activityDocId) => {
  if (!activityDocId) {
    return null;
  }

  try {
    const snapshot = await getDoc(doc(db, 'learning_activities', activityDocId));
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    // Rules allow this read for the owning user (firestore.rules,
    // learning_activities), so a failure here is a signed-out client or a
    // transport problem, not something a server round-trip would recover.
    console.warn('Could not read learning activity details:', {
      activityDocId,
      code: error.code || 'unknown',
      message: error.message,
    });
    return null;
  }
};


/** ONBOARDING TRACKING */

/**
 * Save onboarding completion status to Firebase
 * Called when user completes the onboarding wizard
 */
export const saveOnboardingCompletion = async () => {
  const userId = getCurrentUserId();
  if (!userId) {
    console.warn('No user ID available, cannot save onboarding completion');
    return false;
  }

  try {
    const firestoreUserRef = doc(db, 'users', userId);
    await setDoc(firestoreUserRef, {
      hasCompletedOnboarding: true,
      onboardingCompletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('Onboarding completion saved to Firebase');
    return true;
  } catch (error) {
    console.error('Error saving onboarding completion:', error);
    return false;
  }
};

/**
 * Check if user has completed onboarding
 * Returns { hasCompletedOnboarding, conversationCount } or null if not authenticated
 *
 * Check order (optimized to avoid unnecessary Firebase calls):
 * 1. localStorage hasCompletedOnboarding (fastest - skip Firebase entirely)
 * 2. Firebase: hasCompletedOnboarding field OR 10+ conversations
 * 3. localStorage fallback: hasAcknowledged + userLanguage + targetLanguage
 */
export const checkOnboardingStatus = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot check onboarding status');
    return null;
  }

  // 1. Quick check: localStorage hasCompletedOnboarding (avoids Firebase call)
  if (localStorage.getItem('hasCompletedOnboarding') === 'true') {
    console.log('Onboarding status (localStorage cache): completed');
    return { hasCompletedOnboarding: true, conversationCount: -1 }; // -1 = not fetched
  }

  const userId = user.uid;
  let firebaseHasOnboarding = false;
  let conversationCount = 0;
  let firebaseReadFailed = false;

  try {
    // 2. Check Firebase (primary source)
    const firestoreUserRef = doc(db, 'users', userId);
    const firestoreDoc = await getDoc(firestoreUserRef);

    if (firestoreDoc.exists()) {
      firebaseHasOnboarding = firestoreDoc.data().hasCompletedOnboarding === true;
    }

    // Count user's conversations
    const conversationsRef = collection(db, 'conversations');
    const q = query(conversationsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    conversationCount = querySnapshot.size;

    // If Firebase confirms onboarding OR user has 10+ conversations, cache and return
    if (firebaseHasOnboarding || conversationCount >= 10) {
      // Cache in localStorage to skip Firebase next time
      localStorage.setItem('hasCompletedOnboarding', 'true');

      // If experienced user (10+ convos) but no Firebase flag, save to Firebase too
      if (!firebaseHasOnboarding && conversationCount >= 10) {
        console.log('Experienced user detected, saving onboarding flag to Firebase');
        // Fire and forget - don't block the return
        setDoc(firestoreUserRef, {
          hasCompletedOnboarding: true,
          onboardingCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true }).catch(err => console.error('Error saving experienced user flag:', err));
      }

      console.log('Onboarding status (Firebase):', {
        hasCompletedOnboarding: true,
        source: firebaseHasOnboarding ? 'firebase_flag' : 'experienced_user',
        conversationCount
      });
      return { hasCompletedOnboarding: true, conversationCount };
    }
  } catch (error) {
    console.error('Error fetching from Firebase:', error);
    firebaseReadFailed = true;
    // Continue to localStorage fallback
  }

  // 3. localStorage fallback (legacy check)
  const localStorageCompleted = localStorage.getItem('hasAcknowledged') === 'true';
  const hasUserLanguage = !!localStorage.getItem('userLanguage');
  const hasTargetLanguage = !!localStorage.getItem('targetLanguage');
  const localStorageHasOnboarding = localStorageCompleted && hasUserLanguage && hasTargetLanguage;

  if (localStorageHasOnboarding) {
    // Cache the result to skip checks next time
    localStorage.setItem('hasCompletedOnboarding', 'true');
  }

  console.log('Onboarding status check:', {
    hasCompletedOnboarding: localStorageHasOnboarding,
    firebaseHasOnboarding,
    localStorageHasOnboarding,
    firebaseReadFailed,
    conversationCount
  });

  // A failed read is not evidence that onboarding is needed. Returning
  // `{ hasCompletedOnboarding: false, conversationCount: 0 }` here is
  // indistinguishable from a genuinely new learner, and callers act on it by
  // redirecting to /onboarding — which costs a partner learner their session,
  // because the redirect drops the `?token=` they signed in with. In a
  // partner iframe both sources can be unavailable at once: Firestore over a
  // blocked third-party transport, and localStorage freshly partitioned.
  // Report "unknown" instead; callers already treat null as "do not redirect".
  if (firebaseReadFailed && !localStorageHasOnboarding) {
    console.warn('Onboarding status unknown: Firebase read failed and localStorage has no signal');

    return null;
  }

  return {
    hasCompletedOnboarding: localStorageHasOnboarding,
    conversationCount
  };
};


export { db, auth, createConversationDoc, updateConversationInFirestore, getCurrentUserId, getCurrentUserEmail, getUserCustomBucket, getUserActivityAccess, fetchCurrentUserWork };
